import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Grid } from "@mui/material";

// custom imports
import CustomInput from "../../components/SecondaryForms/CustomInput";
import PageHeader from "../../components/PageHeader";
import FormDialog from "../../forms/FormDialog";
import CustomTable from "../../components/Tables/CustomTable";
import FormInput from "../../forms/FormInput";

// services
import { del, get, post, put } from "../../services/apiMethods";
import URLS from "../../services/urlConstants";

//data
import { updateStatusKeys } from "../../data/TableData/UpdateStatus";
import CustomWarehouseFilter from "../../components/SecondaryForms/CustomWarehouseFilter";
import FormSelect from "../../forms/FormSelect";

export default function UpdateStatus() {
  const [isReady, setIsReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [dataToEdit, setDataToEdit] = useState(null);
  const [data, setData] = useState([]);
  const [formFields, setFormFields] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const [searchFilter, setSearchFilter] = useState([]);
  const [search, setSearch] = useState("");
  const [showSearch, setShowSerach] = useState(true);

  const getData = async () => {
    setIsReady(false);
    try {
      const { data } = await get(URLS.updateStatus.list, {
        params: { warehouse: selectedWarehouse },
      });
      setData(data.result);
    } catch (error) {
      console.log(error);
    }
    setTimeout(() => {
      setIsReady(true);
    }, 300);
  };

  useEffect(() => {
    if (selectedWarehouse) {
      getData();
      getFormDetails();
    } else {
      setIsReady(true);
    }
  }, [selectedWarehouse]);

  const getFormDetails = async () => {
    try {
      const { data } = await get(URLS.forms.read, {
        params: {
          collectionName: "updateStatus",
          warehouse: selectedWarehouse,
        },
      });
      setFormFields(data.result ? data.result.fields : null);
    } catch (error) {
      console.log(error);
    }
  };

  const handleOnFieldAdd = () => {
    getData();
    getFormDetails();
  };

  // create || update actions
  const handleCreateOrUpdate = async (values) => {
    try {
      const payload = {
        ...values,
        meta: formFields,
      };

      setLoading(true);
      if (dataToEdit) {
        const { data } = await put(
          URLS.updateStatus.update + "/" + dataToEdit._id,
          payload
        );
      } else {
        const { data } = await post(URLS.updateStatus.create, payload);
      }

      getData();
      handleModalClose();
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const formik = useFormik({
    initialValues: {
      name: dataToEdit?.name || "",
      description: dataToEdit?.description || "",
      isDefault: dataToEdit?.isDefault || false,
      warehouse: selectedWarehouse || "",
    },
    onSubmit: handleCreateOrUpdate,
    enableReinitialize: true,
  });

  // modal actions
  const openDataAddOrUpdateModal = () => setOpen(true);

  const handleModalClose = () => {
    setOpen(false);
    setTimeout(() => {
      setDataToEdit(null);
      setFormFields(
        formFields?.map((f) => ({
          ...f,
          value: "",
        }))
      );
      formik.resetForm();
    }, 200);
  };

  // edit data actions
  const handleCustomInputChange = (e) => {
    const { name, value } = e.target;
    setFormFields(
      formFields?.map((f) => (f.inputLabel === name ? { ...f, value } : f))
    );
  };

  const handleSetDataToEdit = (data) => {
    setReadOnly(false);
    setDataToEdit(data);
    const fields = data?.meta;
    setFormFields(
      formFields?.map((f) => ({
        ...f,
        value:
          fields?.find((fi) => fi.inputLabel === f.inputLabel)?.value || "",
      }))
    );

    setOpen(true);
  };

  const handleShowData = (data) => {
    setReadOnly(true);
    setDataToEdit(data);
    const fileds = data?.meta;
    setFormFields(
      formFields?.map((f) => ({
        ...f,
        value:
          fileds?.find((fi) => fi.inputLabel === f.inputLabel)?.value || "",
      }))
    );
    setOpen(true);
  };

  const handleDelete = async (id, reason) => {
    try {
      const res = await del(URLS.updateStatus.delete + "/" + id, {
        params: {
          reasonForDelete: reason,
        },
      });
      getData();
    } catch (error) {
      console.log(error);
    }
  };
  return (
    <>
      <PageHeader
        collection="Update Status"
        onAddClick={openDataAddOrUpdateModal}
        title="Update Status"
        modelName="updateStatus"
        onFieldAdd={handleOnFieldAdd}
        module="Update status"
        addButtonDisabled={!selectedWarehouse}
      />

      {/* == warehouse filter components */}
      <CustomWarehouseFilter
        setSelectedWarehouse={setSelectedWarehouse}
        selectedWarehouse={selectedWarehouse}
        data={data}
        setSearch={setSearch}
        setSearchFilter={setSearchFilter}
        search={search}
        placeholder="Name"
        keys={["name"]}
        showSearch={showSearch}
      />

      <CustomTable
        actions={["edit", "delete", "view"]}
        bodyDataModal="update status"
        collection="Update Status"
        bodyData={search ? searchFilter : data}
        isDataReady={isReady}
        tableKeys={updateStatusKeys}
        onDeleteClick={handleDelete}
        onEditClick={handleSetDataToEdit}
        onViewClick={handleShowData}
        module="Update status"
        formFields={formFields}
      />

      <FormDialog
        readOnly={readOnly}
        open={open}
        onClose={handleModalClose}
        submitButtonTitle={dataToEdit ? "Update" : "Add"}
        formik={formik}
        formTitle={dataToEdit ? "Update update status" : "Add update status"}
        adding={loading}
      >
        <Grid container rowSpacing={1} columnSpacing={2}>
          <Grid item xs={12} sm={6}>
            <FormInput
              required={true}
              name="name"
              type="text"
              formik={formik}
              label="Name"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormSelect
              name="isDefault"
              formik={formik}
              label="Is Default"
              options={[
                { value: true, label: "Yes" },
                { value: false, label: "No" },
              ]}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInput
              name="description"
              type="text"
              formik={formik}
              label="Description"
            />
          </Grid>
          {formFields
            ? formFields?.map((f, i) => (
                <Grid key={i} item xs={12} sm={6}>
                  <CustomInput
                    onChange={handleCustomInputChange}
                    name={f.inputLabel}
                    value={f.value}
                    required={f.isRequired}
                    label={f.inputLabel}
                    type={f.inputType}
                  />
                </Grid>
              ))
            : null}
        </Grid>
      </FormDialog>
    </>
  );
}
